/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { App, Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../../components/ts-components/Text'
import { useCallback, useState, useEffect } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import Button from '../../../components/ts-components/Button'
import { CouponForm } from './CouponForm'
import {
  ManageCouponProvider,
  useManageCoupon,
} from './context/ManageCouponContext'
import { useCoupons } from '@sportninja/common/hooks/useCoupons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useModalScroll } from 'src/hooks/useModalScroll'

dayjs.extend(utc)

type ManageCouponModalProps = {
  isFormOpen: boolean
  setIsFormOpen: (isFormOpen: boolean) => void
  scheduleId?: string
  organizationId?: string
  currencyCode?: string
  editingCoupon?: any | null
  onSaveSuccess?: () => void
  /** @deprecated Use editingCoupon/onSaveSuccess instead */
  title?: string
}

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`

const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '0',
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    '@media (max-width: 768px)': {
      overflow: 'hidden',
    },
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
    maxWidth: '95vw',
    width: 'fit-content',
    minWidth: '800px',
    height: '65vh',
    display: 'flex',
    flexDirection: 'column' as const,
    '@media (max-width: 768px)': {
      width: '100%',
      minWidth: 'unset',
      maxWidth: '100%',
      margin: '0',
      borderRadius: '0',
      height: '100vh',
    },
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '16px',
    marginTop: 'auto',
    '@media (max-width: 768px)': {
      position: 'sticky',
      bottom: 0,
      backgroundColor: colors.SECONDARY_300,
      borderTop: `1px solid ${colors.SOFT_STEEL}`,
      marginTop: 'auto',
    },
  },
}

const ManageCouponModalContent = ({
  isFormOpen,
  setIsFormOpen,
  scheduleId,
  organizationId,
  editingCoupon = null,
  onSaveSuccess,
}: ManageCouponModalProps) => {
  const { message } = App.useApp()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Validate that either scheduleId or organizationId is provided
  if (!scheduleId && !organizationId) {
    throw new Error('Either scheduleId or organizationId must be provided')
  }

  if (scheduleId && organizationId) {
    throw new Error('Cannot provide both scheduleId and organizationId')
  }

  const { createCoupon, updateCoupon, refreshCoupons } = useCoupons({
    scheduleId,
    organizationId,
  })
  const {
    id,
    setId,
    code,
    discount,
    discountType,
    isActive,
    startsAtMonth,
    startsAtDay,
    startsAtYear,
    endsAtMonth,
    endsAtDay,
    endsAtYear,
    maxUses,
    setFieldErrors,
    setCustomError,
    setCode,
    setDiscount,
    setDiscountType,
    setIsActive,
    setStartsAtMonth,
    setStartsAtDay,
    setStartsAtYear,
    setEndsAtMonth,
    setEndsAtDay,
    setEndsAtYear,
    setMaxUses,
    emailRestrictions,
    setEmailRestrictions,
    isTeam,
    setIsTeam,
    isPlayer,
    setIsPlayer,
    isDeposit,
    setIsDeposit,
    isInstallment,
    setIsInstallment,
  } = useManageCoupon()
  const { openModal, closeModal } = useModalScroll()

  useEffect(() => {
    if (isFormOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isFormOpen, openModal])

  const clearFormState = useCallback(() => {
    setId('')
    setCode('')
    setDiscount('0')
    setDiscountType('percentage')
    setIsActive(true)
    setStartsAtMonth('')
    setStartsAtDay('')
    setStartsAtYear('')
    setEndsAtMonth('')
    setEndsAtDay('')
    setEndsAtYear('')
    setMaxUses(0)
    setEmailRestrictions([])
    setIsTeam(false)
    setIsPlayer(false)
    setIsDeposit(true)
    setIsInstallment(true)
    setFieldErrors([])
    setCustomError({
      code: '',
      discount: '',
      discount_type: 'percentage',
      is_active: true,
      starts_at: '',
      ends_at: '',
    })
  }, [
    setId,
    setCode,
    setDiscount,
    setDiscountType,
    setIsActive,
    setStartsAtMonth,
    setStartsAtDay,
    setStartsAtYear,
    setEndsAtMonth,
    setEndsAtDay,
    setEndsAtYear,
    setMaxUses,
    setFieldErrors,
    setCustomError,
    setEmailRestrictions,
    setIsTeam,
    setIsPlayer,
    setIsDeposit,
    setIsInstallment,
  ])

  // Pre-populate form when editingCoupon changes
  useEffect(() => {
    if (!isFormOpen) {
      return
    }

    if (editingCoupon) {
      setId(editingCoupon.id)
      setCode(editingCoupon.code)
      setDiscount(editingCoupon.discount.toString())
      setDiscountType(editingCoupon.discount_type)
      setIsActive(editingCoupon.is_active)
      setMaxUses(editingCoupon.max_uses || 0)

      if (editingCoupon.start_date) {
        const startDate = dayjs(editingCoupon.start_date).utc()
        setStartsAtMonth(String(startDate.month() + 1).padStart(2, '0'))
        setStartsAtDay(String(startDate.date()).padStart(2, '0'))
        setStartsAtYear(startDate.year().toString())
      } else {
        setStartsAtMonth('')
        setStartsAtDay('')
        setStartsAtYear('')
      }

      if (editingCoupon.end_date) {
        const endDate = dayjs(editingCoupon.end_date).utc()
        setEndsAtMonth(String(endDate.month() + 1).padStart(2, '0'))
        setEndsAtDay(String(endDate.date()).padStart(2, '0'))
        setEndsAtYear(endDate.year().toString())
      } else {
        setEndsAtMonth('')
        setEndsAtDay('')
        setEndsAtYear('')
      }

      if (editingCoupon?.email_matches) {
        setEmailRestrictions(editingCoupon.email_matches?.split(','))
      }
      setIsTeam(!!editingCoupon?.is_team)
      setIsPlayer(!!editingCoupon?.is_player)
      setIsInstallment(!!editingCoupon?.is_installment)
      setIsDeposit(!!editingCoupon?.is_deposit)
    } else {
      clearFormState()
    }
  }, [
    isFormOpen,
    editingCoupon,
    clearFormState,
    setId,
    setCode,
    setDiscount,
    setDiscountType,
    setIsActive,
    setMaxUses,
    setStartsAtMonth,
    setStartsAtDay,
    setStartsAtYear,
    setEndsAtMonth,
    setEndsAtDay,
    setEndsAtYear,
    setEmailRestrictions,
    setIsTeam,
    setIsPlayer,
    setIsInstallment,
    setIsDeposit,
  ])

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true)
    try {
      // Discount value must be greater than 0
      if (Number(discount) <= 0 || discount === '') {
        setCustomError((prev) => ({
          ...prev,
          discount: 'Discount value must be greater than 0',
        }))
        setFieldErrors(['discount'])
        return
      }

      let isTeamCoupon = isTeam
      let isPlayerCoupon = isPlayer

      // If both are false, them this coupons applies to both
      // types of registrations
      if (!isTeamCoupon && !isPlayerCoupon) {
        isTeamCoupon = true
        isPlayerCoupon = true
      }

      const payload: any = {
        code,
        discount: Number(discount),
        discount_type: discountType,
        is_active: isActive,
        is_team: isTeamCoupon,
        is_player: isPlayerCoupon,
        is_deposit: isDeposit,
        is_installment: isInstallment,
      }

      // Only add dates if all components are present
      if (startsAtYear && startsAtMonth && startsAtDay) {
        payload.start_date = `${startsAtYear}-${startsAtMonth}-${startsAtDay}`
      } else {
        payload.start_date = null
      }

      if (endsAtYear && endsAtMonth && endsAtDay) {
        payload.end_date = `${endsAtYear}-${endsAtMonth}-${endsAtDay}`
      } else {
        payload.end_date = null
      }

      // Start date must be before end date
      if (payload.start_date && payload.end_date) {
        const startDate = new Date(payload.start_date)
        const endDate = new Date(payload.end_date)
        if (startDate > endDate) {
          setCustomError((prev) => ({
            ...prev,
            starts_at: 'Start Date must be before End Date',
          }))
          setFieldErrors(['starts_at'])
          return
        }
      }

      // Only add max_uses if greater than 0
      if (maxUses > 0) {
        payload.max_uses = maxUses
      }

      // Email restrictions must be an array
      if (emailRestrictions?.length > 0) {
        payload.email_matches = emailRestrictions.join(',')
      } else {
        payload.email_matches = ''
      }

      if (id) {
        // if max_uses is 0, remove it from the payload
        if (maxUses === 0) {
          payload.max_uses = null
        }
        await updateCoupon(id, payload)
        message.success(`Coupon ${code} has been updated.`)
      } else {
        await createCoupon(payload)
      }

      if (onSaveSuccess) {
        onSaveSuccess()
      } else {
        refreshCoupons()
        setIsFormOpen(false)
      }
      clearFormState()
    } catch (e: any) {
      if (e?.invalid_fields) {
        const errors: string[] = []
        Object.keys(e.invalid_fields).forEach((field) => {
          setCustomError((prev) => ({
            ...prev,
            [field]: e.invalid_fields[field],
          }))
          errors.push(field)
        })
        setFieldErrors(errors)
      } else {
        message.error(e?.message)
        setTimeout(() => {
          message.destroy()
        }, 3000)
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [
    code,
    discount,
    discountType,
    isActive,
    startsAtYear,
    startsAtMonth,
    startsAtDay,
    endsAtYear,
    endsAtMonth,
    endsAtDay,
    maxUses,
    emailRestrictions,
    id,
    refreshCoupons,
    clearFormState,
    setCustomError,
    setFieldErrors,
    updateCoupon,
    message,
    createCoupon,
    isTeam,
    isPlayer,
    isDeposit,
    isInstallment,
    onSaveSuccess,
    setIsFormOpen,
  ])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          align-items: center;
          width: 100%;
          @media (max-width: 768px) {
            flex-direction: column;
            gap: 8px;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 8px;
            width: 50%;
            align-self: flex-end;
            @media (max-width: 768px) {
              width: 100%;
              flex-direction: column;
            }
          `}
        >
          <Button
            text='CANCEL'
            onClick={() => {
              setIsFormOpen(false)
              clearFormState()
            }}
            variant='secondary'
            disabled={isSubmitting}
          />
          <Button
            text={isSubmitting ? 'SAVING...' : id ? 'SAVE CHANGES' : 'SAVE'}
            onClick={handleSubmit}
            variant='primary'
            disabled={isSubmitting}
          />
        </div>
      </div>
    )
  }, [isSubmitting, id, clearFormState, setIsFormOpen, handleSubmit])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        {id ? 'Edit Coupon' : 'New Coupon'}
      </Text>
    )
  }, [id])

  return (
    <Modal
      css={css`
        height: 60vh;
        max-height: 620px;
        .ant-modal-content {
          height: 60vh;
          max-height: 620px;
          overflow: hidden;
          @media (max-width: 768px) {
            width: 100%;
            min-width: unset !important;
          }
        }
        @media (max-width: 768px) {
          width: 100%;
          margin: 0;
          top: 0;
          padding-bottom: 0;
          .ant-modal {
            top: 0;
            padding-bottom: 0;
            margin: 0;
          }
        }
      `}
      width='auto'
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isFormOpen}
      onOk={() => {
        setIsFormOpen(false)
      }}
      onCancel={() => {
        setIsFormOpen(false)
        clearFormState()
      }}
      centered
      classNames={classNames}
      styles={modalStyles}
      maskClosable={false}
    >
      <div
        css={css`
          padding: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          @media (max-width: 768px) {
            padding: 8px;
          }
        `}
      >
        <CouponForm />
      </div>
    </Modal>
  )
}

const ManageCouponModal = (props: ManageCouponModalProps) => {
  return (
    <ManageCouponProvider>
      <ManageCouponModalContent {...props} />
    </ManageCouponProvider>
  )
}

export default ManageCouponModal
